import { formatDollars } from '@fruitful/fruitils';
import {
    type DebtAccount,
    DEPOSIT_APY_PERCENT,
    formatSaveForGoalLabel,
    getScenarioSplits,
    HIGH_INTEREST_THRESHOLD,
    INVESTMENT_RETURN_PERCENT,
    IRA_ANNUAL_LIMIT_CENTS,
    MAX_SIMULATION_MONTHS,
    type MoneyMapSimulation,
    type RuleSet,
    runSimulation,
    type SimulationParams,
    type SimulationResult,
} from '@fruitful/money-map-simulator';
import {
    type AhaMomentIntake,
    type DebtSubAccountInfo,
    type RuleCompletionInfo,
    type RuleSetDisplay,
    RuleSetType,
    RuleType,
} from '@fruitful/types';

export {
    DEPOSIT_APY_PERCENT,
    INVESTMENT_RETURN_PERCENT,
    MAX_SIMULATION_MONTHS,
    type RuleCompletionInfo,
    type RuleSetDisplay,
};
export const GOAL_DATE_CUTOFF_YEARS = 10;

function formatDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
    });
}

/**
 * Estimate payoff date for a debt that didn't pay off within the simulation
 * window. Uses the remaining balance, interest rate, and effective monthly
 * payment (derived from balance trajectory) to extrapolate.
 */
function extrapolateDebtPayoffDate(
    remainingBalance: number,
    monthlyRate: number,
    lastMonthDate: Date,
    prevBalance: number,
    interestApplied: number,
): string | null {
    if (remainingBalance <= 0) return null;

    const effectivePayment = prevBalance + interestApplied - remainingBalance;
    if (effectivePayment <= remainingBalance * monthlyRate) return null;

    let extraMonths: number;
    if (monthlyRate > 0.0001) {
        extraMonths = Math.ceil(
            -Math.log(1 - (remainingBalance * monthlyRate) / effectivePayment) /
                Math.log(1 + monthlyRate),
        );
    } else {
        extraMonths = Math.ceil(remainingBalance / effectivePayment);
    }

    const estimated = new Date(lastMonthDate);
    estimated.setMonth(estimated.getMonth() + extraMonths);
    return formatDate(estimated);
}

function computeAvalancheRuleCompletion(
    rule: {
        type: RuleType;
        description: string;
        splitPercentage: number;
        accountIds?: string[];
        allocationType: string;
    },
    simulation: MoneyMapSimulation,
    result: SimulationResult,
): RuleCompletionInfo {
    const accountIds = rule.accountIds ?? [];
    const debtAccounts = accountIds
        .map((id) => simulation.accounts.find((a) => a.id === id))
        .filter((a): a is DebtAccount => a?.type === 'DEBT');

    const totalDebtCents = debtAccounts.reduce(
        (sum, d) => sum + d.balanceCents,
        0,
    );

    const payoffs = debtAccounts.map((debt) => {
        let payoffDate: string | null = null;
        let payoffMonthIndex: number | null = null;
        for (const [i, month] of result.months.entries()) {
            const balance = month.balancesAfter[debt.id] ?? 0;
            if (balance <= 0) {
                payoffDate = formatDate(month.date);
                payoffMonthIndex = i;
                break;
            }
        }

        if (payoffDate == null && result.months.length >= 2) {
            const lastMonth = result.months.at(-1);
            const prevMonth = result.months.at(-2);
            if (lastMonth != null && prevMonth != null) {
                const remaining = lastMonth.balancesAfter[debt.id] ?? 0;
                const prevBal = prevMonth.balancesAfter[debt.id] ?? 0;
                const interest = lastMonth.interestApplied[debt.id] ?? 0;
                payoffDate = extrapolateDebtPayoffDate(
                    remaining,
                    debt.annualInterestRate / 12,
                    lastMonth.date,
                    prevBal,
                    interest,
                );
            }
        }

        return {
            name: debt.name,
            balanceCents: debt.balanceCents,
            annualInterestRate: debt.annualInterestRate,
            completionDate: payoffDate,
            payoffMonthIndex,
        };
    });

    const subAccounts: DebtSubAccountInfo[] = payoffs.map(
        ({ payoffMonthIndex: _payoffMonthIndex, ...subAccount }) => subAccount,
    );

    const completedPayoffs = payoffs.filter(
        (
            p,
        ): p is typeof p & {
            completionDate: string;
            payoffMonthIndex: number;
        } => p.completionDate != null && p.payoffMonthIndex != null,
    );

    const lastPayoff =
        completedPayoffs.length > 0
            ? completedPayoffs[completedPayoffs.length - 1]
            : null;

    const lastPayoffWithDate = [...payoffs]
        .reverse()
        .find((p) => p.completionDate != null);
    const overallCompletionDate =
        lastPayoff?.completionDate ??
        lastPayoffWithDate?.completionDate ??
        null;

    const completionLabel =
        lastPayoff != null &&
        lastPayoff.payoffMonthIndex <= GOAL_DATE_CUTOFF_YEARS * 12
            ? lastPayoff.completionDate
            : null;

    return {
        type: rule.type,
        description: rule.description,
        splitPercentage: rule.splitPercentage,
        targetDisplay: `Total debt: ${formatDollars(totalDebtCents / 100, false)}`,
        completionDate: overallCompletionDate,
        completionLabel,
        allocationType: rule.allocationType,
        subAccounts,
        targetBalanceCents: totalDebtCents,
    };
}

export interface RuleSetDisplayResult {
    ruleSets: RuleSetDisplay[];
    exceedsDateThreshold: boolean;
    emergencyFundUnreachable: boolean;
    noGoalsMoney: boolean;
}

export function computeRuleSetDisplays(
    simulation: MoneyMapSimulation,
): RuleSetDisplayResult {
    const params: SimulationParams = {
        startDate: new Date(),
        numMonths: MAX_SIMULATION_MONTHS,
        depositApyPercent: DEPOSIT_APY_PERCENT,
        investmentReturnPercent: INVESTMENT_RETURN_PERCENT,
    };

    const result = runSimulation(simulation, params);

    const debtAccountIds = simulation.accounts
        .filter((a) => a.type === 'DEBT')
        .map((a) => a.id);

    let exceedsDateThreshold = false;
    if (debtAccountIds.length > 0) {
        const thresholdMonths = GOAL_DATE_CUTOFF_YEARS * 12;
        const allPaidInTime = debtAccountIds.every((id) => {
            for (
                let i = 0;
                i < Math.min(thresholdMonths, result.months.length);
                i++
            ) {
                const month = result.months[i];
                if (month != null && (month.balancesAfter[id] ?? 0) <= 0) {
                    return true;
                }
            }
            return false;
        });
        exceedsDateThreshold = !allPaidInTime;
    }

    const ruleSets = simulation.ruleSets.map((ruleSet: RuleSet) => ({
        name: ruleSet.name,
        priority: ruleSet.priority,
        type: ruleSet.type,
        rules: ruleSet.rules.map((rule) => {
            if (
                rule.allocationType === 'DEBT_PAY_DOWN' &&
                rule.strategy === 'AVALANCHE' &&
                rule.accountIds != null
            ) {
                return computeAvalancheRuleCompletion(rule, simulation, result);
            }

            const accountId = rule.accountId ?? '';
            const account = simulation.accounts.find((a) => a.id === accountId);

            let targetDisplay = '';
            let completionDate: string | null = null;
            let completionMonthIndex: number | null = null;

            if (rule.allocationType === 'MINIMUM_BALANCE') {
                targetDisplay = `Target: ${formatDollars(rule.targetBalanceCents / 100, false)}`;
                for (const [i, month] of result.months.entries()) {
                    const balance = month.balancesAfter[accountId] ?? 0;
                    if (balance >= rule.targetBalanceCents) {
                        completionDate = formatDate(month.date);
                        completionMonthIndex = i;
                        break;
                    }
                }
            } else if (rule.allocationType === 'DEBT_PAY_DOWN') {
                const debtAccount = account;
                if (debtAccount != null && 'balanceCents' in debtAccount) {
                    targetDisplay = `Total debt: ${formatDollars(debtAccount.balanceCents / 100, false)}`;
                }
                for (const [i, month] of result.months.entries()) {
                    const balance = month.balancesAfter[accountId] ?? 0;
                    if (balance <= 0) {
                        completionDate = formatDate(month.date);
                        completionMonthIndex = i;
                        break;
                    }
                }
                if (
                    completionDate == null &&
                    debtAccount != null &&
                    'annualInterestRate' in debtAccount &&
                    result.months.length >= 2
                ) {
                    const lastMonth = result.months.at(-1);
                    const prevMonth = result.months.at(-2);
                    if (lastMonth != null && prevMonth != null) {
                        const remaining =
                            lastMonth.balancesAfter[accountId] ?? 0;
                        const prevBal = prevMonth.balancesAfter[accountId] ?? 0;
                        const interest =
                            lastMonth.interestApplied[accountId] ?? 0;
                        const rate = debtAccount.annualInterestRate / 12;
                        const extrapolated = extrapolateDebtPayoffDate(
                            remaining,
                            rate,
                            lastMonth.date,
                            prevBal,
                            interest,
                        );
                        if (extrapolated != null) {
                            completionDate = extrapolated;
                        }
                    }
                }
            } else if (rule.allocationType === 'PERCENTAGE') {
                if (
                    rule.yearlyContributionMaxCents != null &&
                    rule.yearlyContributionMaxCents > 0
                ) {
                    targetDisplay = `Target: ${formatDollars(rule.yearlyContributionMaxCents / 100, false)}/yr`;
                } else if (
                    'monthlyContributionMaxCents' in rule &&
                    rule.monthlyContributionMaxCents != null &&
                    rule.monthlyContributionMaxCents > 0
                ) {
                    targetDisplay = `Target: ${formatDollars(rule.monthlyContributionMaxCents / 100, false)}/mo`;
                } else {
                    const monthlyContribution = Math.round(
                        simulation.monthlyGoalsCents * rule.splitPercentage,
                    );
                    targetDisplay = `Target: ${formatDollars((monthlyContribution * 12) / 100, false)}/yr`;
                }
            }

            // Only show the pill when there's an actual computed date within
            // the cutoff. "Funded by…" without a date adds no value — and for
            // IRA rules (ongoing annual contributions) a date never applies.
            // Goals further out than the cutoff are hidden per-goal, so a
            // waterfall can show some dated pills and omit others.
            const completionLabel =
                completionDate != null &&
                completionMonthIndex != null &&
                completionMonthIndex <= GOAL_DATE_CUTOFF_YEARS * 12
                    ? completionDate
                    : null;

            return {
                type: rule.type,
                description: rule.description,
                splitPercentage: rule.splitPercentage,
                targetDisplay,
                completionDate,
                completionLabel,
                allocationType: rule.allocationType,
                targetBalanceCents: rule.targetBalanceCents,
                monthlyContributionMaxCents: rule.monthlyContributionMaxCents,
                yearlyContributionMaxCents: rule.yearlyContributionMaxCents,
            };
        }),
    }));

    let emergencyFundUnreachable = false;
    const efRules = ruleSets
        .flatMap((rs) => rs.rules)
        .filter((r) => r.allocationType === 'MINIMUM_BALANCE');
    if (efRules.length > 0) {
        emergencyFundUnreachable = efRules.some(
            (r) => r.completionDate == null,
        );
    }

    const hasDebt = debtAccountIds.length > 0;
    if (hasDebt && (exceedsDateThreshold || emergencyFundUnreachable)) {
        for (const rs of ruleSets) {
            const isFirstPriority = rs.priority === 1;
            if (
                isFirstPriority &&
                rs.name.toLowerCase().includes('emergency')
            ) {
                rs.name = 'Starter Emergency Fund';
            }
            for (const rule of rs.rules) {
                if (
                    isFirstPriority &&
                    rule.allocationType === 'MINIMUM_BALANCE' &&
                    rule.description.toLowerCase().includes('emergency')
                ) {
                    rule.description = rule.description.replace(
                        /\d+-month emergency fund/i,
                        'Starter emergency fund',
                    );
                }
            }
        }
    }

    return {
        ruleSets,
        exceedsDateThreshold,
        emergencyFundUnreachable,
        noGoalsMoney: false,
    };
}

export function getPrimaryRuleDescription(ruleSet: RuleSetDisplay): string {
    const sorted = [...ruleSet.rules].sort(
        (a, b) => b.splitPercentage - a.splitPercentage,
    );
    const primary = sorted[0];
    if (primary == null) return '';

    const others = sorted.slice(1);
    const otherNames = others
        .map((r) => r.description.toLowerCase())
        .join(' and ');

    let primaryAction = primary.description.toLowerCase();
    if (primary.allocationType === 'DEBT_PAY_DOWN') {
        const debtAmount = primary.targetDisplay.replace('Total debt: ', '');
        const isHighInterest = primary.description
            .toLowerCase()
            .includes('high-interest');
        primaryAction = isHighInterest
            ? `pay off your ${debtAmount} high interest debt`
            : `pay off your ${debtAmount} in debt`;
    } else if (primary.allocationType === 'MINIMUM_BALANCE') {
        primaryAction = `build your ${primary.description.toLowerCase()}`;
    }

    if (others.length > 0) {
        return `Your primary goal will be to ${primaryAction}, while contributing to your ${otherNames}.`;
    }

    return `Your primary goal will be to ${primaryAction}.`;
}

/**
 * Build rule set displays directly from intake data, without running
 * a simulation. Used as a fallback when the simulation can't run
 * (e.g. no balances provided). All dates are omitted.
 */
export function buildStaticRuleSetDisplays(
    intake: AhaMomentIntake,
): RuleSetDisplay[] {
    const debts = intake.debts ?? [];
    const savings = intake.savings ?? [];
    const hasDebts = debts.length > 0;
    const hasSavings = savings.length > 0;

    if (!hasDebts && !hasSavings) return [];

    const hasHighInterest = debts.some(
        (d) =>
            d.annualInterestRatePercent != null &&
            d.annualInterestRatePercent / 100 > HIGH_INTEREST_THRESHOLD,
    );

    const totalDebtCents = debts.reduce(
        (sum, d) => sum + (d.balanceRemainingCents ?? 0),
        0,
    );

    const subAccounts: DebtSubAccountInfo[] = debts.map((d) => ({
        name: d.description ?? 'Debt',
        balanceCents: d.balanceRemainingCents ?? 0,
        annualInterestRate: (d.annualInterestRatePercent ?? 0) / 100,
        completionDate: null,
    }));

    const billsPlusSpend =
        intake.monthlyExpensesCents + intake.monthlySpendCents;

    const splits = getScenarioSplits({
        hasDebts,
        hasSavings,
        hasHighInterest,
    });

    const financialHealthRules: RuleCompletionInfo[] = [];

    if (hasDebts) {
        financialHealthRules.push({
            description: hasHighInterest
                ? 'Pay off high-interest debt'
                : 'Pay off debt',
            type: RuleType.DebtPayDown,
            splitPercentage: splits.debt,
            targetDisplay:
                totalDebtCents > 0
                    ? `Total debt: ${formatDollars(totalDebtCents / 100, false)}`
                    : '',
            completionDate: null,
            completionLabel: null,
            allocationType: 'DEBT_PAY_DOWN',
            subAccounts,
        });
    }

    if (hasSavings) {
        const MAX_REASONABLE_TARGET_CENTS = 20_000_000; // $200k
        const under = savings.filter(
            (g) => g.targetAmountCents <= MAX_REASONABLE_TARGET_CENTS,
        );
        const goal =
            under.length > 0
                ? under.reduce((best, g) =>
                      g.targetAmountCents > best.targetAmountCents ? g : best,
                  )
                : savings.reduce((best, g) =>
                      g.targetAmountCents < best.targetAmountCents ? g : best,
                  );
        const targetCents = goal?.targetAmountCents ?? 0;
        financialHealthRules.push({
            description: formatSaveForGoalLabel(goal?.description),
            type: RuleType.Savings,
            splitPercentage: splits.savings,
            targetDisplay:
                targetCents > 0
                    ? `Target: ${formatDollars(targetCents / 100, false)}`
                    : '',
            completionDate: null,
            completionLabel: null,
            allocationType: 'MINIMUM_BALANCE',
        });
    }

    financialHealthRules.push({
        description: 'Build 3-month emergency fund',
        type: RuleType.FullEmergencyFund,
        splitPercentage: splits.ef,
        targetDisplay: `Target: ${formatDollars((3 * billsPlusSpend) / 100, false)}`,
        completionDate: null,
        completionLabel: null,
        allocationType: 'MINIMUM_BALANCE',
    });

    financialHealthRules.push({
        description: 'Retirement contributions',
        type: RuleType.Retirement,
        splitPercentage: splits.retirement,
        targetDisplay: `Target: ${formatDollars(IRA_ANNUAL_LIMIT_CENTS / 100, false)}/yr`,
        completionDate: null,
        completionLabel: null,
        allocationType: 'PERCENTAGE',
    });

    return [
        {
            name: 'Starter Emergency Fund',
            type: RuleSetType.EmergencyFund,
            priority: 1,
            rules: [
                {
                    type: RuleType.StarterEmergencyFund,
                    description: 'Starter emergency fund',
                    splitPercentage: 1.0,
                    targetDisplay: `Target: ${formatDollars(billsPlusSpend / 100, false)}`,
                    completionDate: null,
                    completionLabel: null,
                    allocationType: 'MINIMUM_BALANCE',
                },
            ],
        },
        {
            name: 'Financial Health',
            type: RuleSetType.FinancialHealth,
            priority: 2,
            rules: financialHealthRules,
        },
    ];
}
